import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Tooltip,
  Stack,
  Menu,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  NavigateBefore,
  NavigateNext,
  Home,
  FlightTakeoff,
  Refresh,
  SportsSoccer,
  Schedule,
  CalendarMonth,
  Download,
  Event as EventIcon,
  LocationOn,
} from "@mui/icons-material";
import * as XLSX from "xlsx";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import { PageToolbar, ModalShell, useNotify } from "components/Convokko";
import { DEPORTES, deporteLabel } from "config";

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const DAYS_OF_WEEK = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];

const timeToMinutes = (time) => {
  if (!time) return 0;
  const [hours, minutes] = time.split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

// Fusiona partidos duplicados (mismo encuentro visto desde los dos equipos propios)
// y marca conflictos de horario.
const removeDuplicateEvents = (events) => {
  const seen = new Map();
  const result = [];
  const conflicts = new Map();

  events.forEach((event) => {
    const uniqueKey = `${event.id}-${event.team}`;
    const internalMatchKey = `${event.date}-${event.time}-${event.location}-${event.deporte}`;
    const conflictKey = `${event.date}-${event.time}-${event.location}`;

    if (seen.has(uniqueKey)) return;
    seen.set(uniqueKey, event);

    const potentialInternalMatch = result.find((e) => {
      const eInternalKey = `${e.date}-${e.time}-${e.location}-${e.deporte}`;
      return eInternalKey === internalMatchKey && e.local !== event.local;
    });

    if (potentialInternalMatch) {
      potentialInternalMatch.isInternalMatch = true;
      potentialInternalMatch.homeTeam =
        event.local === "casa" ? event.teamName : potentialInternalMatch.teamName;
      potentialInternalMatch.awayTeam =
        event.local === "fuera" ? event.teamName : potentialInternalMatch.teamName;
      potentialInternalMatch.homeTeamId =
        event.local === "casa" ? event.team : potentialInternalMatch.team;
      potentialInternalMatch.awayTeamId =
        event.local === "fuera" ? event.team : potentialInternalMatch.team;
      potentialInternalMatch.local = "interno";
      potentialInternalMatch.opponent = `${potentialInternalMatch.homeTeam} vs ${potentialInternalMatch.awayTeam}`;
    } else {
      result.push(event);

      if (conflicts.has(conflictKey)) {
        const conflictingEvents = conflicts.get(conflictKey);
        const hasRealConflict = conflictingEvents.some(
          (e) => e.local === event.local || e.deporte !== event.deporte
        );
        if (hasRealConflict) {
          conflictingEvents.push(event);
          conflictingEvents.forEach((e) => {
            if (!e.isInternalMatch) {
              e.hasScheduleConflict = true;
              e.conflictCount = conflictingEvents.length;
            }
          });
        } else {
          conflicts.set(conflictKey, [event]);
        }
      } else {
        conflicts.set(conflictKey, [event]);
      }
    }
  });

  return result;
};

export default function SoccerSchoolCalendar() {
  const notify = useNotify();
  const { temporada } = useSeason();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSchool, setSelectedSchool] = useState("");
  const [deporteFiltro, setDeporteFiltro] = useState("");
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncPeriod, setSyncPeriod] = useState(null);
  const [selectedTeamForSync, setSelectedTeamForSync] = useState("");

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch("/api/escuelas/partidos");
      const transformed = (Array.isArray(data) ? data : []).map((event) => ({
        id: event.id,
        date: event.fecha,
        type: "partido",
        local: event.local ? "casa" : "fuera",
        location: event.ubicacion || "No especificado",
        opponent: event.oponente,
        time: event.hora || "00:00",
        team: event.equipo,
        teamName: event.nombre_equipo,
        jornada: event.jornada,
        competicion: event.competicion,
        temporada: event.temporada,
        deporte: event.deporte,
        resultado: event.resultado || "",
      }));
      setEvents(removeDuplicateEvents(transformed));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const matchesFilters = useCallback(
    (event) => {
      const matchesSeason = !event.temporada || Number(event.temporada) === Number(temporada);
      const matchesTeam = selectedSchool
        ? event.isInternalMatch
          ? event.homeTeamId === selectedSchool || event.awayTeamId === selectedSchool
          : event.team === selectedSchool
        : true;
      const matchesDeporte = deporteFiltro ? event.deporte === deporteFiltro : true;
      return matchesSeason && matchesTeam && matchesDeporte;
    },
    [temporada, selectedSchool, deporteFiltro]
  );

  const equiposParaFiltro = useMemo(() => {
    const teamsMap = new Map();
    events.forEach((event) => {
      if (event.isInternalMatch) {
        if (event.homeTeamId && event.homeTeam)
          teamsMap.set(event.homeTeamId, { id: event.homeTeamId, nombre: event.homeTeam });
        if (event.awayTeamId && event.awayTeam)
          teamsMap.set(event.awayTeamId, { id: event.awayTeamId, nombre: event.awayTeam });
      } else if (event.team && event.teamName) {
        teamsMap.set(event.team, { id: event.team, nombre: event.teamName });
      }
    });
    return Array.from(teamsMap.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [events]);

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];
    for (let i = 0; i < firstDayOfWeek; i++) days.push({ day: null, isCurrentMonth: false });
    for (let day = 1; day <= daysInMonth; day++) days.push({ day, isCurrentMonth: true });
    const remaining = 7 - (days.length % 7);
    if (remaining < 7)
      for (let i = 0; i < remaining; i++) days.push({ day: null, isCurrentMonth: false });
    return days;
  };

  const getEventsForDay = useCallback(
    (day) => {
      if (!day) return [];
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;
      return events
        .filter((event) => event.date === dateStr && matchesFilters(event))
        .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
    },
    [events, currentDate, matchesFilters]
  );

  const monthEvents = useMemo(
    () =>
      events.filter((event) => {
        const eventDate = new Date(event.date);
        return (
          eventDate.getMonth() === currentDate.getMonth() &&
          eventDate.getFullYear() === currentDate.getFullYear() &&
          matchesFilters(event)
        );
      }),
    [events, currentDate, matchesFilters]
  );

  const getCurrentWeekEvents = () => {
    const today = new Date(currentDate);
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return events
      .filter((event) => {
        const eventDate = new Date(event.date);
        return eventDate >= monday && eventDate <= sunday && matchesFilters(event);
      })
      .sort((a, b) => {
        const dateCompare = new Date(a.date) - new Date(b.date);
        if (dateCompare !== 0) return dateCompare;
        return timeToMinutes(a.time) - timeToMinutes(b.time);
      });
  };

  // --- Exportaciones ---
  const downloadEventsToExcel = (period) => {
    const eventsToExport = period === "week" ? getCurrentWeekEvents() : monthEvents;
    if (eventsToExport.length === 0) {
      notify.warning(
        `No hay partidos para exportar en ${period === "week" ? "esta semana" : "este mes"}`
      );
      setDownloadMenuAnchor(null);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const excelData = eventsToExport.map((event) => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      const resultado = event.resultado || "";
      const sinResultado = !resultado || resultado === "0-0" || resultado === "-";
      let estado;
      if (eventDate < today) estado = sinResultado ? "Pendiente" : "Finalizado";
      else if (eventDate > today) estado = "No Jugado";
      else estado = sinResultado ? "En Curso" : "Finalizado";

      return {
        Fecha: event.date,
        Hora: event.time || "Sin hora",
        Equipo: event.isInternalMatch ? `${event.homeTeam} vs ${event.awayTeam}` : event.teamName,
        Oponente: event.isInternalMatch ? "Partido Interno" : event.opponent,
        Tipo: event.isInternalMatch ? "Interno" : event.local === "casa" ? "Casa" : "Fuera",
        Ubicación: event.location,
        Competición: event.competicion,
        Jornada: event.jornada,
        Deporte: deporteLabel(event.deporte),
        Temporada: event.temporada,
        Resultado: resultado || "-",
        Estado: estado,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Partidos");
    worksheet["!cols"] = [
      { wch: 12 },
      { wch: 8 },
      { wch: 25 },
      { wch: 25 },
      { wch: 10 },
      { wch: 30 },
      { wch: 30 },
      { wch: 10 },
      { wch: 15 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
    ];

    const monthName = MONTHS[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    XLSX.writeFile(
      workbook,
      period === "week"
        ? `Partidos_Semana_${monthName}_${year}.xlsx`
        : `Partidos_${monthName}_${year}.xlsx`
    );
    setDownloadMenuAnchor(null);
  };

  const downloadEventsToCalendar = (period, teamId) => {
    let eventsToExport = period === "week" ? getCurrentWeekEvents() : monthEvents;

    if (teamId) {
      eventsToExport = eventsToExport.filter((event) =>
        event.isInternalMatch
          ? event.homeTeamId === teamId || event.awayTeamId === teamId
          : event.team === teamId
      );
    }

    if (eventsToExport.length === 0) {
      notify.warning("No hay partidos para exportar con esos criterios");
      return;
    }

    const formatICalDateTime = (date, time) => {
      const [hours, minutes] = (time || "00:00").split(":");
      const eventDate = new Date(date);
      eventDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      const y = eventDate.getFullYear();
      const m = String(eventDate.getMonth() + 1).padStart(2, "0");
      const d = String(eventDate.getDate()).padStart(2, "0");
      const h = String(eventDate.getHours()).padStart(2, "0");
      const min = String(eventDate.getMinutes()).padStart(2, "0");
      return `${y}${m}${d}T${h}${min}00`;
    };

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Convokko//Calendario//ES",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "X-WR-CALNAME:Partidos Convokko",
      "X-WR-TIMEZONE:Europe/Madrid",
    ];

    eventsToExport.forEach((event, index) => {
      const startDateTime = formatICalDateTime(event.date, event.time);
      const eventDate = new Date(event.date);
      const [hours, minutes] = (event.time || "00:00").split(":");
      eventDate.setHours(parseInt(hours, 10) + 1, parseInt(minutes, 10), 0, 0);
      const endDateTime = formatICalDateTime(
        event.date,
        `${String(eventDate.getHours()).padStart(2, "0")}:${String(eventDate.getMinutes()).padStart(
          2,
          "0"
        )}`
      );

      const summary = event.isInternalMatch
        ? `${event.homeTeam} vs ${event.awayTeam}`
        : `${event.teamName} vs ${event.opponent}`;

      const description = [
        event.isInternalMatch
          ? "Partido Interno"
          : event.local === "casa"
          ? "Partido en Casa"
          : "Partido Fuera",
        `Competición: ${event.competicion}`,
        `Jornada: ${event.jornada}`,
        `Deporte: ${deporteLabel(event.deporte)}`,
      ].join("\\n");

      icsContent.push(
        "BEGIN:VEVENT",
        `UID:${event.id}-${index}@convokko.com`,
        `DTSTAMP:${formatICalDateTime(new Date().toISOString().split("T")[0], "00:00")}`,
        `DTSTART:${startDateTime}`,
        `DTEND:${endDateTime}`,
        `SUMMARY:${summary}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${event.location}`,
        "STATUS:CONFIRMED",
        "END:VEVENT"
      );
    });

    icsContent.push("END:VCALENDAR");

    const blob = new Blob([icsContent.join("\r\n")], { type: "text/calendar;charset=utf-8" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    const teamName = teamId
      ? equiposParaFiltro.find((t) => t.id === teamId)?.nombre || "Equipo"
      : "Todos";
    const monthName = MONTHS[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    link.download =
      syncPeriod === "week"
        ? `Partidos_${teamName}_Semana_${monthName}_${year}.ics`
        : `Partidos_${teamName}_${monthName}_${year}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);

    setSyncDialogOpen(false);
    setSelectedTeamForSync("");
    setDownloadMenuAnchor(null);
  };

  const eventColor = (event) => {
    if (event.isInternalMatch) return "#8b5cf6";
    return event.local === "casa" ? "#F59E0B" : "#1A73E8";
  };

  const eventIcon = (event, size = 15) => {
    if (event.isInternalMatch) return <SportsSoccer sx={{ fontSize: size }} />;
    return event.local === "casa" ? (
      <Home sx={{ fontSize: size }} />
    ) : (
      <FlightTakeoff sx={{ fontSize: size }} />
    );
  };

  const renderCalendarDay = (dayObj, index) => {
    const { day, isCurrentMonth } = dayObj;

    if (!isCurrentMonth || !day) {
      return (
        <Box
          key={`empty-${index}`}
          sx={{
            minHeight: { xs: 70, sm: 84, md: 96 },
            borderRight: "1px solid",
            borderBottom: "1px solid",
            borderColor: "grey.100",
            bgcolor: "grey.100",
            opacity: 0.35,
          }}
        />
      );
    }

    const dayEvents = getEventsForDay(day);
    const today = new Date();
    const isToday =
      today.getDate() === day &&
      today.getMonth() === currentDate.getMonth() &&
      today.getFullYear() === currentDate.getFullYear();
    const hasEvents = dayEvents.length > 0;

    return (
      <Box
        key={`day-${day}`}
        onClick={() => hasEvents && setSelectedDay({ day, events: dayEvents })}
        sx={{
          minHeight: { xs: 70, sm: 84, md: 96 },
          p: 0.5,
          borderRight: "1px solid",
          borderBottom: "1px solid",
          borderColor: "grey.100",
          cursor: hasEvents ? "pointer" : "default",
          bgcolor: isToday ? "rgba(245,158,11,.07)" : "#fff",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          transition: "background .15s ease",
          "&:hover": hasEvents ? { bgcolor: "grey.100" } : {},
        }}
      >
        <Typography
          variant="caption"
          sx={{
            alignSelf: "flex-end",
            fontSize: { xs: "0.65rem", sm: "0.72rem" },
            fontWeight: isToday ? 800 : 500,
            color: isToday ? "#B45309" : "text.secondary",
            width: 20,
            height: 20,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isToday ? "rgba(245,158,11,.18)" : "transparent",
          }}
        >
          {day}
        </Typography>

        {dayEvents.length === 1 && (
          <Stack alignItems="center" spacing={0.4} mt={0.5}>
            <Box
              sx={{
                display: { xs: "block", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 8, sm: 24 },
                height: { xs: 8, sm: 24 },
                borderRadius: "50%",
                bgcolor: eventColor(dayEvents[0]),
                color: "#fff",
              }}
            >
              <Box sx={{ display: { xs: "none", sm: "flex" } }}>{eventIcon(dayEvents[0], 13)}</Box>
            </Box>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.6rem", fontWeight: 700, lineHeight: 1, color: "text.primary" }}
            >
              {dayEvents[0].time || "--:--"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.5rem",
                color: "text.secondary",
                lineHeight: 1,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: { xs: "none", sm: "block" },
              }}
            >
              {dayEvents[0].isInternalMatch ? "Interno" : dayEvents[0].teamName}
            </Typography>
          </Stack>
        )}

        {dayEvents.length > 1 && (
          <Stack alignItems="center" spacing={0.4} mt={0.5}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.4}
              sx={{
                bgcolor: "secondary.main",
                color: "#fff",
                borderRadius: 5,
                px: 0.75,
                py: 0.15,
              }}
            >
              <Schedule sx={{ fontSize: 11 }} />
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, lineHeight: 1.4 }}>
                {dayEvents.length}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.55rem",
                fontWeight: 600,
                color: "text.secondary",
                display: { xs: "none", sm: "block" },
              }}
            >
              partidos
            </Typography>
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={CalendarMonth}
          title="Calendario"
          count={monthEvents.length}
          countLabel="partidos este mes"
          filters={[
            {
              id: "equipo",
              label: "Equipo",
              allLabel: "Todos los equipos",
              value: selectedSchool,
              onChange: setSelectedSchool,
              options: equiposParaFiltro.map((e) => ({ value: e.id, label: e.nombre })),
              minWidth: 140,
            },
            {
              id: "deporte",
              label: "Deporte",
              allLabel: "Todos los deportes",
              value: deporteFiltro,
              onChange: setDeporteFiltro,
              options: DEPORTES,
              minWidth: 110,
            },
          ]}
        />

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 3 }}
            action={
              <Button color="inherit" size="small" onClick={fetchEvents}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "grey.200",
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          {/* Cabecera: mes + navegación + acciones */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={{ xs: 1.5, sm: 2.5 }}
            py={1.75}
            borderBottom="1px solid"
            borderColor="grey.200"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Recargar partidos">
                <span>
                  <IconButton
                    onClick={fetchEvents}
                    disabled={loading}
                    size="small"
                    sx={{
                      background: "#F59E0B",
                      color: "#fff",
                      "&:hover": { opacity: 0.9 },
                      "&.Mui-disabled": { bgcolor: "grey.300", background: "none" },
                    }}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Exportar / sincronizar">
                <IconButton
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={{ border: "1.5px solid", borderColor: "grey.300" }}
                >
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton onClick={() => navigateMonth(-1)} size="small">
                <NavigateBefore />
              </IconButton>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ minWidth: { xs: 130, sm: 170 }, textAlign: "center" }}
              >
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </Typography>
              <IconButton onClick={() => navigateMonth(1)} size="small">
                <NavigateNext />
              </IconButton>
            </Stack>

            <Button
              size="small"
              onClick={() => setCurrentDate(new Date())}
              sx={{
                borderRadius: 2,
                color: "text.secondary",
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              Hoy
            </Button>
          </Stack>

          {/* Leyenda */}
          <Stack
            direction="row"
            spacing={1}
            px={{ xs: 1.5, sm: 2.5 }}
            py={1}
            flexWrap="wrap"
            useFlexGap
            borderBottom="1px solid"
            borderColor="grey.100"
          >
            <Chip
              icon={<Home sx={{ fontSize: "13px !important" }} />}
              label="Casa"
              size="small"
              variant="outlined"
              sx={{
                borderColor: "#F59E0B",
                color: "#B45309",
                fontWeight: 600,
                fontSize: "0.7rem",
                "& .MuiChip-icon": { color: "#F59E0B" },
              }}
            />
            <Chip
              icon={<FlightTakeoff sx={{ fontSize: "13px !important" }} />}
              label="Fuera"
              size="small"
              variant="outlined"
              sx={{
                borderColor: "#1A73E8",
                color: "#1A73E8",
                fontWeight: 600,
                fontSize: "0.7rem",
                "& .MuiChip-icon": { color: "#1A73E8" },
              }}
            />
            <Chip
              icon={<SportsSoccer sx={{ fontSize: "13px !important" }} />}
              label="Interno"
              size="small"
              variant="outlined"
              sx={{
                borderColor: "#8b5cf6",
                color: "#8b5cf6",
                fontWeight: 600,
                fontSize: "0.7rem",
                "& .MuiChip-icon": { color: "#8b5cf6" },
              }}
            />
          </Stack>

          {loading ? (
            <Box p={2}>
              <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
            </Box>
          ) : (
            <>
              {/* Días de la semana */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                {DAYS_OF_WEEK.map((d) => (
                  <Typography
                    key={d}
                    variant="caption"
                    textAlign="center"
                    py={0.75}
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ borderBottom: "1px solid", borderColor: "grey.200", fontSize: "0.7rem" }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>
              {/* Celdas */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                {getDaysInMonth().map((dayObj, index) => renderCalendarDay(dayObj, index))}
              </Box>
            </>
          )}
        </Card>
      </Box>

      {/* Menú de exportación */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => downloadEventsToExcel("week")}>Excel · esta semana</MenuItem>
        <MenuItem onClick={() => downloadEventsToExcel("month")}>Excel · este mes</MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setSyncPeriod("week");
            setSyncDialogOpen(true);
            setDownloadMenuAnchor(null);
          }}
        >
          Calendario (.ics) · esta semana
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSyncPeriod("month");
            setSyncDialogOpen(true);
            setDownloadMenuAnchor(null);
          }}
        >
          Calendario (.ics) · este mes
        </MenuItem>
      </Menu>

      {/* Diálogo del día */}
      <ModalShell
        open={Boolean(selectedDay)}
        onClose={() => setSelectedDay(null)}
        title={selectedDay ? `${selectedDay.day} de ${MONTHS[currentDate.getMonth()]}` : ""}
        subtitle={`${selectedDay?.events?.length || 0} partidos`}
        icon={EventIcon}
      >
        <Stack spacing={1.5}>
          {(selectedDay?.events || []).map((event, i) => (
            <Card
              key={`${event.id}-${i}`}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: event.hasScheduleConflict ? "error.main" : "grey.200",
                borderRadius: 3,
                p: 2,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: eventColor(event),
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {eventIcon(event, 18)}
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
                    {event.isInternalMatch
                      ? `${event.homeTeam} vs ${event.awayTeam}`
                      : `${event.teamName} vs ${event.opponent}`}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mt={0.75}>
                    <Chip
                      label={event.time || "--:--"}
                      size="small"
                      sx={{ fontWeight: 600, bgcolor: "grey.100" }}
                    />
                    {event.competicion && (
                      <Chip
                        label={`${event.competicion} · J${event.jornada}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    <Chip label={deporteLabel(event.deporte)} size="small" variant="outlined" />
                    {event.resultado && event.resultado !== "-" && (
                      <Chip
                        label={event.resultado}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                    {event.hasScheduleConflict && (
                      <Chip
                        label="Conflicto horario"
                        size="small"
                        color="error"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                  {event.location && (
                    <Stack direction="row" spacing={0.5} alignItems="center" mt={0.75}>
                      <LocationOn sx={{ fontSize: 15, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        {event.location}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Card>
          ))}
        </Stack>
      </ModalShell>

      {/* Diálogo de sincronización ICS */}
      <ModalShell
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        title="Exportar a calendario"
        subtitle={syncPeriod === "week" ? "Partidos de esta semana" : "Partidos de este mes"}
        icon={CalendarMonth}
        color="info"
        actions={
          <>
            <Button onClick={() => setSyncDialogOpen(false)} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => downloadEventsToCalendar(syncPeriod, selectedTeamForSync)}
              startIcon={<Download />}
              sx={{ borderRadius: 2 }}
            >
              Descargar .ics
            </Button>
          </>
        }
      >
        <Stack spacing={2} mt={0.5}>
          <Typography variant="body2" color="text.secondary">
            Descarga un archivo compatible con Google Calendar, Apple Calendar y Outlook. Puedes
            exportar todos los equipos o solo uno.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Equipo</InputLabel>
            <Select
              value={selectedTeamForSync}
              label="Equipo"
              onChange={(e) => setSelectedTeamForSync(e.target.value)}
              sx={{ height: 44 }}
            >
              <MenuItem value="">Todos los equipos</MenuItem>
              {equiposParaFiltro.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </ModalShell>

      <Footer />
    </DashboardLayout>
  );
}
